<script>
  import { onDestroy } from "svelte";
  import Icon from "@iconify/svelte";
  import { t } from "../lib/i18n";

  import storageIcon from "@iconify/icons-mdi/folder-outline";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import closeIcon from "@iconify/icons-mdi/close";
  import downloadIcon from "@iconify/icons-mdi/download-outline";
  import openInNewIcon from "@iconify/icons-mdi/open-in-new";
  import folderIcon from "@iconify/icons-mdi/folder-outline";
  import fileIcon from "@iconify/icons-mdi/file-outline";
  import alertIcon from "@iconify/icons-mdi/alert-circle-outline";

  export let apiBase = "";
  export let apiKey = ""; // NEW

  const JSON_HEADERS = { Accept: "application/json", "Cache-Control": "no-store" };

  // /setup
  let setup = null;
  let setupErr = "";
  let setupLoading = false;

  // navigation state
  let path = ""; // relative path under outputPrefix
  let selectedRoot = "";

  // left pane (roots)
  let roots = [];
  let rootsLoading = false;
  let rootsErr = "";

  // drawer
  let drawerOpen = false;

  // right pane listing
  let listing = null;
  let listingLoading = false;
  let listingErr = "";
  let selectedPreviewKey = "";

  // preview state
  let previewUrl = "";
  let previewLoading = false;
  let previewErr = "";
  let previewAbort = null;
  let lastPreviewPath = "";

  function normalizePath(p) {
    return (p || "").toString().replace(/^\/+|\/+$/g, "");
  }

  function joinPath(base, seg) {
    const b = normalizePath(base);
    const s = normalizePath(seg);
    if (!b) return s;
    if (!s) return b;
    return `${b}/${s}`;
  }

  function downloadURL(p) {
    const clean = normalizePath(p);
    if (!clean) return "#";
    const encoded = clean
      .split("/")
      .filter(Boolean)
      .map(encodeURIComponent)
      .join("/");
    return `${apiBase}/download/${encoded}`;
  }

  function crumbs(p) {
    const clean = normalizePath(p);
    if (!clean) return [];
    const parts = clean.split("/");
    return parts.map((name, i) => ({
      name,
      path: parts.slice(0, i + 1).join("/")
    }));
  }

  function ts(v) {
    if (!v) return 0;
    const d = new Date(v);
    const n = d.getTime();
    return Number.isFinite(n) ? n : 0;
  }

  function fmtTime(v) {
    if (!v) return "—";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  }

  function fmtBytes(n) {
    const v = Number(n || 0);
    if (!Number.isFinite(v) || v <= 0) return "—";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let x = v;
    let u = 0;
    while (x >= 1024 && u < units.length - 1) {
      x /= 1024;
      u++;
    }
    return `${x.toFixed(u === 0 ? 0 : 1)} ${units[u]}`;
  }

  function isImagePath(p) {
    const clean = normalizePath(p).toLowerCase();
    return /\.(png|jpe?g|gif|webp|avif|bmp|tiff?|heic|heif|jxl)$/i.test(clean);
  }

  function previewEntryOf(entries) {
    const arr = Array.isArray(entries) ? entries : [];
    return (
      arr.find((e) => !e?.is_dir && isImagePath(e?.key || e?.rel_key || e?.name)) ||
      arr.find((e) => !e?.is_dir) ||
      null
    );
  }

  function getEnv(name) {
    const env = setup?.env || [];
    const hit = env.find((e) => e.name === name);
    return hit?.value || hit?.default || "";
  }

  function goUp() {
    if (!path) return;
    const parts = path.split("/").filter(Boolean);
    parts.pop();
    openPath(parts.join("/"));
  }

  $: outputBucket = getEnv("S3_OUTPUT_BUCKET");
  $: outputPrefix = getEnv("S3_OUTPUT_PREFIX");
  $: storageBackend = getEnv("STORAGE_BACKEND");

  // ---- auth header (always send; API ignores when disabled) ----
  function jsonAuthHeaders() {
    const h = { ...JSON_HEADERS };
    const k = (apiKey ?? "").toString().trim();
    if (k) h.Authorization = `Bearer ${k}`;
    return h;
  }

  // ---- hash routing (#/storage/<path>) ----
  function readHashPath() {
    const h = (location.hash || "").trim();
    if (!h.startsWith("#/storage")) return "";
    return normalizePath(h.replace(/^#\/storage\/?/, ""));
  }

  function setHashPath(p) {
    const clean = normalizePath(p);
    const next = clean ? `#/storage/${clean}` : "#/storage";
    if (location.hash !== next) location.hash = next;
  }

  function applyPath(p) {
    const clean = normalizePath(p);
    path = clean;
    selectedRoot = clean.split("/")[0] || "";
  }

  function onHashChange() {
    const p = readHashPath();
    if (p !== path) {
      applyPath(p);
      if (setup) {
        drawerOpen = true;
        loadListing(path);
      }
    }
  }

  if (typeof window !== "undefined") {
    if (!window.__intern_storage_hash_listener_attached) {
      window.__intern_storage_hash_listener_attached = true;
      window.addEventListener("hashchange", onHashChange);
    }
  }

  async function loadSetup() {
    setupLoading = true;
    setupErr = "";
    setup = null;

    try {
      const res = await fetch(`${apiBase}/setup`, {
        cache: "no-store",
        headers: jsonAuthHeaders()
      });
      if (!res.ok) throw new Error(`setup failed: ${res.status}`);
      setup = await res.json();
    } catch (e) {
      setupErr = e?.message || String(e);
    } finally {
      setupLoading = false;
    }
  }

  async function loadRoots() {
    rootsLoading = true;
    rootsErr = "";
    roots = [];

    try {
      const res = await fetch(`${apiBase}/storage`, {
        cache: "no-store",
        headers: jsonAuthHeaders()
      });
      let body = null;
      try {
        body = await res.json();
      } catch {}
      if (!res.ok) throw new Error(body?.error || `storage failed: ${res.status}`);

      const dirs = (body?.entries || []).filter((e) => e?.is_dir);

      // newest first if backend provides timestamps for dirs (often empty today)
      dirs.sort(
        (a, b) =>
          ts(b?.last_modified) - ts(a?.last_modified) ||
          (a?.name || "").localeCompare(b?.name || "")
      );

      roots = dirs;
    } catch (e) {
      rootsErr = e?.message || String(e);
    } finally {
      rootsLoading = false;
    }
  }

  async function loadListing(nextPath) {
    const p = normalizePath(nextPath);
    applyPath(p);
    selectedPreviewKey = "";

    listingLoading = true;
    listingErr = "";
    listing = null;

    // IMPORTANT: don't encode slashes; /storage/{path...} expects slashy paths.
    // Encode each segment, not the whole string.
    const encoded = p
      ? p
          .split("/")
          .filter(Boolean)
          .map(encodeURIComponent)
          .join("/")
      : "";

    const url = encoded ? `${apiBase}/storage/${encoded}` : `${apiBase}/storage`;

    try {
      const res = await fetch(url, {
        cache: "no-store",
        headers: jsonAuthHeaders()
      });
      let body = null;
      try {
        body = await res.json();
      } catch {}
      if (!res.ok) throw new Error(body?.error || `storage failed: ${res.status}`);

      listing = body;
    } catch (e) {
      listingErr = e?.message || String(e);
    } finally {
      listingLoading = false;
    }
  }

  function openRoot(name) {
    const p = normalizePath(name);
    drawerOpen = true;
    setHashPath(p);
    loadListing(p);
  }

  function openRootEntry(entry) {
    if (!entry) return;
    if (entry.is_dir) {
      openRoot(entry.name);
      return;
    }
    selectedPreviewKey = normalizePath(entry.key || entry.name);
    downloadFile(normalizePath(entry.key || entry.name), entry.name);
  }

  function selectPreview(entry) {
    if (!entry || entry.is_dir) return;
    selectedPreviewKey = normalizePath(entry.key || entry.name);
  }

  function openPath(p) {
    const clean = normalizePath(p);
    drawerOpen = true;
    selectedPreviewKey = "";
    setHashPath(clean);
    loadListing(clean);
  }

  function closeDrawer() {
    drawerOpen = false;
    // Keep hash/path as-is (URL tracking). If you want “close clears”, uncomment:
    // setHashPath("");
    selectedPreviewKey = "";
    clearPreview();
  }

  function clearPreview() {
    try { previewAbort?.abort?.(); } catch {}
    previewAbort = null;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = "";
    previewLoading = false;
    previewErr = "";
  }

  async function downloadFile(p, name) {
    const res = await fetch(downloadURL(p), {
      cache: "no-store",
      headers: jsonAuthHeaders()
    });
    if (!res.ok) {
      let msg = `download failed: ${res.status}`;
      try {
        const body = await res.text();
        if (body) msg = body;
      } catch {}
      listingErr = msg;
      return;
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name || p.split("/").filter(Boolean).pop() || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function loadPreview(path) {
    const clean = normalizePath(path);
    if (!clean || !isImagePath(clean)) {
      clearPreview();
      return;
    }

    const encoded = clean
      .split("/")
      .filter(Boolean)
      .map(encodeURIComponent)
      .join("/");

    try { previewAbort?.abort?.(); } catch {}
    previewAbort = new AbortController();
    previewLoading = true;
    previewErr = "";

    try {
      const res = await fetch(`${apiBase}/download/${encoded}`, {
        cache: "no-store",
        signal: previewAbort.signal,
        headers: jsonAuthHeaders()
      });
      if (!res.ok) throw new Error(`preview failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = url;
    } catch (e) {
      if (e?.name === "AbortError") return;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = "";
      previewErr = e?.message || String(e);
    } finally {
      previewLoading = false;
    }
  }

  $: breadcrumb = crumbs(path);

  $: listingEntries = (() => {
    const arr = Array.isArray(listing?.entries) ? [...listing.entries] : [];
    arr.sort((a, b) => {
      const dt = ts(b?.last_modified) - ts(a?.last_modified);
      if (dt !== 0) return dt;
      if (!!a?.is_dir !== !!b?.is_dir) return a?.is_dir ? -1 : 1;
      return (a?.name || "").localeCompare(b?.name || "");
    });
    return arr;
  })();

  $: previewPath = (() => {
    const base = path;
    if (!base) return "";
    const current = selectedPreviewKey
      ? listingEntries.find((e) => normalizePath(e.key || e.name) === selectedPreviewKey)
      : null;
    const entry = current || previewEntryOf(listingEntries);
    if (!entry || entry.is_dir) return "";
    return joinPath(base, entry.name);
  })();

  $: {
    if (previewPath !== lastPreviewPath) {
      lastPreviewPath = previewPath;
      if (previewPath) loadPreview(previewPath);
      else clearPreview();
    }
  }

  onDestroy(() => {
    clearPreview();
  });

  // initial load (same pattern as Results.svelte which auto-loads)
  (async () => {
    // avoid re-running on HMR by keying on setup already loaded
    if (setup || setupLoading) return;

    applyPath(readHashPath());

    await loadSetup();
    if (!setup) return;

    await loadRoots();

    // auto-open drawer if URL has a path
    if (path) {
      drawerOpen = true;
      await loadListing(path);
    } else {
      drawerOpen = false;
      listing = null;
    }
  })();

  // UI tokens (copied style vibe from Results.svelte)
  const headerChip =
    "rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-zinc-300 inline-flex items-center gap-2";

  const btn =
    "inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-zinc-200 hover:bg-white/[0.06] disabled:opacity-50 disabled:cursor-not-allowed";

  const mono =
    "font-mono text-xs text-zinc-200 whitespace-nowrap overflow-hidden text-ellipsis";
</script>

<main class="w-full px-4 py-6">
  <div class="flex gap-4">
    <!-- MAIN LIST -->
    <section
      class={`ui-card p-5 sm:p-6 min-w-0 flex-1 ${
        drawerOpen ? "lg:mr-[520px]" : ""
      }`}
    >
      <!-- Header -->
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-3">
          <span
            class="inline-flex h-9 w-9 items-center justify-center rounded border border-emerald-400/50 bg-emerald-500/30 text-emerald-50"
          >
            <Icon icon={storageIcon} width="18" height="18" />
          </span>

          <div>
            <h1 class="text-lg font-normal tracking-wide text-zinc-100">
              {$t('storage.title')}
            </h1>
            <p class="mt-0.5 text-sm text-zinc-400">
              {$t('storage.subtitle')}
            </p>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <div class={headerChip} title={$t('storage.backend')}>
            <span class="text-zinc-400">{$t('storage.backend')}</span>
            <span class="font-medium text-zinc-200">{storageBackend || "—"}</span>
          </div>

          <div class={headerChip} title={$t('storage.bucket')}>
            <span class="text-zinc-400">{$t('storage.bucket')}</span>
            <span class="font-medium text-zinc-200">{outputBucket || "—"}</span>
          </div>

          <div class={headerChip} title={$t('storage.prefix')}>
            <span class="text-zinc-400">{$t('storage.prefix')}</span>
            <span class="font-medium text-zinc-200">{outputPrefix || "—"}</span>
          </div>

          <button
            class={btn}
            on:click={loadRoots}
            disabled={rootsLoading || setupLoading}
            title={$t('storage.refresh_folders')}
          >
            <Icon icon={refreshIcon} width="18" height="18" />
          </button>
        </div>
      </div>

      {#if setupErr}
        <div
          class="mt-4 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100"
        >
          {setupErr}
        </div>
      {/if}

      {#if rootsErr}
        <div
          class="mt-4 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100"
        >
          {rootsErr}
        </div>
      {/if}

      <div class="mt-6 text-xs text-zinc-500">
        {$t('storage.results_land')}:
        <span class="font-mono text-zinc-300"
          >s3://{outputBucket || "<bucket>"}/
          {outputPrefix ? outputPrefix.replace(/\/?$/, "/") : ""}&lt;output_key&gt;/</span
        >
      </div>

      <!-- Folder list -->
      <div
        class="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden"
      >
        <div
          class="px-4 py-3 border-b border-white/10 flex items-center justify-between"
        >
          <div class="text-sm font-semibold text-zinc-100">{$t('storage.folders')}</div>
          <div class="text-xs text-zinc-500">
            {rootsLoading ? $t('common.loading') : `${roots.length} ${$t('common.items')}`}
          </div>
        </div>

        <div class="max-h-[65vh] overflow-auto">
          {#if setupLoading}
            <div class="p-4 text-sm text-zinc-400">{$t('storage.loading_setup')}</div>
          {:else if rootsLoading}
            <div class="p-4 text-sm text-zinc-400">{$t('storage.loading_folders')}</div>
          {:else if !roots?.length}
            <div class="p-4 text-sm text-zinc-400">{$t('storage.no_folders')}</div>
          {:else}
            <div class="p-3 grid gap-2">
              {#each roots as r (r.key)}
                <button
                  type="button"
                  class={`w-full text-left border transition-colors p-4 ${
                    selectedRoot === r.name
                      ? "border-[rgba(52,211,153,0.70)] bg-gradient-to-r from-[rgba(52,211,153,0.18)] via-[rgba(52,211,153,0.08)] to-transparent ring-1 ring-[rgba(52,211,153,0.55)] relative before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-[rgb(52,211,153)] before:shadow-[0_0_22px_rgba(52,211,153,0.95)]"
                      : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"

                  }`}
                  on:click={() => openRootEntry(r)}
                  title={r.key}
                >
                  <div class="flex items-center justify-between gap-3">
                    <div class="min-w-0 flex items-center gap-2">
                      <Icon
                        icon={folderIcon}
                        width="18"
                        height="18"
                        class="shrink-0 text-zinc-300"
                      />
                      <div class="min-w-0">
                        <div class="text-sm text-zinc-100 truncate">
                          {r.name}
                        </div>
                        <div class="mt-1 text-xs text-zinc-500">
                          {r.last_modified ? fmtTime(r.last_modified) : "—"}
                        </div>
                      </div>
                    </div>

                    <div class="text-[11px] text-zinc-500 shrink-0">{$t('common.open')}</div>
                  </div>
                </button>
              {/each}
            </div>
          {/if}
        </div>
      </div>
    </section>

    <!-- RIGHT DRAWER (not modal) -->
    {#if drawerOpen}
      <aside
        class="hidden lg:block fixed right-0 top-[88px] bottom-0 w-[520px]
               border-l border-white/10 bg-black/40 backdrop-blur-xl"
      >
        <div class="h-full overflow-auto p-6">
          <!-- Card -->
          <div class="rounded-xl border border-white/5 bg-white/[0.02] p-5">
            <!-- Header -->
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <div class="text-xs font-medium uppercase tracking-wide text-zinc-400">
                  {$t('storage.contents')}
                </div>

                <div class="mt-1 text-xs text-zinc-500 truncate" title={path || "root"}>
                  {path || "root"}
                </div>
              </div>

              <!-- Controls -->
              <div class="flex items-center gap-2 shrink-0">
                <a
                  class="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200"
                  href={path
                    ? `${apiBase}/storage/${path
                        .split("/")
                        .filter(Boolean)
                        .map(encodeURIComponent)
                        .join("/")}`
                    : `${apiBase}/storage`}
                  target="_blank"
                  rel="noreferrer"
                  title={$t('storage.open_json')}
                >
                  <Icon icon={openInNewIcon} width="16" height="16" />
                  <span>json</span>
                </a>

                <button class={btn} on:click={goUp} disabled={!path} title={$t('storage.go_up')}>
                  <Icon icon="mdi:arrow-up" width="18" height="18" />
                </button>

                <button
                  class={btn}
                  on:click={() => loadListing(path)}
                  disabled={listingLoading}
                  title={$t('storage.refresh_contents')}
                >
                  <Icon icon={refreshIcon} width="18" height="18" />
                </button>

                <button class={btn} on:click={closeDrawer} title={$t('common.close')}>
                  <Icon icon={closeIcon} width="18" height="18" />
                </button>
              </div>
            </div>

            <div class="mt-4 rounded-xl border border-white/10 bg-black/20 p-3">
              <div class="text-xs font-medium uppercase tracking-wide text-zinc-400">
                {$t('storage.contents')}
              </div>
              {#if previewLoading}
                <div class="mt-2 h-28 rounded-lg border border-white/10 bg-black/30 grid place-items-center text-xs text-zinc-500">
                  {$t('common.loading')}
                </div>
              {:else if previewUrl}
                <img
                  src={previewUrl}
                  alt={selectedPreviewKey || path || $t('common.root')}
                  class="mt-2 h-28 w-full rounded-lg border border-white/10 object-cover bg-black/30"
                />
              {:else}
                <div class="mt-2 h-28 rounded-lg border border-white/10 bg-black/30 grid place-items-center text-xs text-zinc-500">
                  {previewErr || $t('common.no_items')}
                </div>
              {/if}
            </div>

            <!-- Divider -->
            <div class="mt-4 border-t border-white/10 pt-4">
              <!-- Breadcrumbs -->
              <div class="text-xs text-zinc-500 flex flex-wrap items-center gap-2">
                <span class="text-zinc-600">{$t('common.path')}:</span>

                <a class="hover:text-white" href="#/storage"
                  on:click|preventDefault={() => openPath("")}>
                  {$t('common.root')}
                </a>

                {#each breadcrumb as c}
                  <span class="text-zinc-700">/</span>
                  <a
                    class="hover:text-white"
                    href={`#/storage/${c.path}`}
                    on:click|preventDefault={() => openPath(c.path)}
                  >
                    {c.name}
                  </a>
                {/each}
              </div>
            </div>

            <!-- Errors -->
            {#if listingErr}
              <div
                class="mt-5 rounded-xl border border-rose-400/35
                       bg-rose-400/10 px-4 py-3 text-sm text-rose-100
                       flex items-start gap-2"
              >
                <Icon icon={alertIcon} width="18" height="18" class="shrink-0 mt-0.5" />
                <div class="min-w-0">{listingErr}</div>
              </div>
            {/if}

            <!-- Loading -->
            {#if listingLoading}
              <div class="mt-5 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400">
                {$t('common.loading')}
              </div>
            {:else if listing}
              <!-- Meta -->
              <div class="mt-5 text-xs text-zinc-500">
                bucket=<span class="font-mono text-zinc-300">{listing.bucket}</span>
                &nbsp;prefix=<span class="font-mono text-zinc-300">{listing.prefix}</span>
                &nbsp;count=<span class="font-mono text-zinc-300">{listing.count}</span>
              </div>

              <!-- Table -->
              <div class="mt-3 rounded-xl border border-white/10 overflow-hidden">
                <div class="max-h-[70vh] overflow-auto">
                  <table class="w-full text-sm">
                    <thead class="bg-black/20 text-xs text-zinc-500">
                      <tr>
                        <th class="text-left p-3">{$t('storage.name')}</th>
                        <th class="text-left p-3">{$t('storage.type')}</th>
                        <th class="text-right p-3">{$t('storage.size')}</th>
                        <th class="text-left p-3">{$t('storage.modified')}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {#each listingEntries as e (e.key)}
                        <tr class="border-t border-white/10 hover:bg-white/[0.04]">
                          <td class="p-3 min-w-0">
                            {#if e.is_dir}
                              <button
                                type="button"
                                class="inline-flex items-center gap-2 text-zinc-200 hover:text-white"
                                on:click={() => openPath(joinPath(path, e.name))}
                                title={e.key}
                              >
                                <Icon icon={folderIcon} width="16" height="16" />
                                <span class="font-semibold">
                                  {e.rel_key || `${e.name}/`}
                                </span>
                              </button>
                            {:else}
                              <div class="flex items-center justify-between gap-3 min-w-0" title={e.key}>
                                <button
                                  type="button"
                                  class="flex items-center gap-2 min-w-0 text-left hover:text-white"
                                  on:click={() => selectPreview(e)}
                                  title={e.key}
                                >
                                  <Icon
                                    icon={fileIcon}
                                    width="16"
                                    height="16"
                                    class="shrink-0 text-zinc-400"
                                  />
                                  <span class="truncate">{e.rel_key || e.name}</span>
                                </button>

                                <button
                                  type="button"
                                  class="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-400/25 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/15 hover:text-emerald-100"
                                  on:click={() => downloadFile(joinPath(path, e.name), e.name)}
                                  title={$t('storage.download_file')}
                                  aria-label={$t('storage.download_file')}
                                >
                                  <Icon icon={downloadIcon} width="16" height="16" />
                                </button>
                              </div>
                            {/if}
                          </td>

                          <td class="p-3 text-xs text-zinc-500">
                            {e.is_dir ? $t('common.dir') : $t('common.file')}
                          </td>

                          <td class="p-3 text-right text-xs text-zinc-500">
                            {e.is_dir ? "—" : fmtBytes(e.size)}
                          </td>

                          <td class="p-3 text-xs text-zinc-500">
                            {e.is_dir ? "—" : fmtTime(e.last_modified)}
                          </td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              </div>
            {/if}
          </div>
        </div>
      </aside>
    {/if}
  </div>
</main>
